import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import Header from '@/components/storefront/Header';
import { Helmet } from 'react-helmet-async';
import Footer from '@/components/storefront/Footer';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Package, CheckCircle, Truck, MapPin, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const CARRIER_URLS = {
  USPS: (t) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${t}`,
  UPS: (t) => `https://www.ups.com/track?tracknum=${t}`,
  FedEx: (t) => `https://www.fedex.com/fedextrack/?trknbr=${t}`,
  DHL: (t) => `https://www.dhl.com/en/express/tracking.html?AWB=${t}`,
};

const statusSteps = ['confirmed', 'processing', 'shipped', 'in_transit', 'delivered'];
const statusLabels = { confirmed: 'Confirmed', processing: 'Processing', shipped: 'Shipped', in_transit: 'In Transit', delivered: 'Delivered', refunded: 'Refunded', assigned: 'Assigned' };

export default function TrackOrderPage() {
  const [searchParams] = useSearchParams();
  const [orderNum, setOrderNum] = useState(searchParams.get('order') || '');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleTrack = async () => {
    if (!orderNum.trim()) { toast.error('Order number is required'); return; }
    setLoading(true);
    setSearched(true);
    try {
      const params = email ? `?email=${encodeURIComponent(email)}` : '';
      const { data } = await api.get(`/orders/track/${orderNum.trim()}${params}`);
      setOrder(data);
    } catch {
      setOrder(null);
      toast.error('Order not found. Check your order number and email.');
    }
    setLoading(false);
  };

  // Auto-track if params present
  useEffect(() => {
    if (searchParams.get('order') && searchParams.get('email')) {
      handleTrack();
    }
  }, []);

  const currentStepIdx = order ? Math.max(0, statusSteps.indexOf(order.fulfillment_status)) : 0;
  const carrierUrl = order?.tracking_number && order?.carrier && CARRIER_URLS[order.carrier]
    ? CARRIER_URLS[order.carrier](order.tracking_number) : null;

  return (
    <div data-testid="track-order-page" className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Helmet><title>Track Your Order - Florashop</title></Helmet>
      <Header />
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="text-center mb-10">
          <h1 className="font-serif text-3xl tracking-tight mb-2" style={{ color: '#1A1A1A' }}>Track Your Order</h1>
          <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.5)' }}>Enter your order number and email to see the status</p>
        </motion.div>

        {/* Search form */}
        <div className="flex flex-col sm:flex-row gap-3 mb-10">
          <Input data-testid="track-order-number" value={orderNum} onChange={e => setOrderNum(e.target.value)}
            placeholder="Order number (e.g. 1001)" className="font-sans text-sm rounded-none flex-1" style={{ borderColor: 'rgba(26,26,26,0.12)' }}
            onKeyDown={e => e.key === 'Enter' && handleTrack()} />
          <Input data-testid="track-email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="Email (optional)" className="font-sans text-sm rounded-none flex-1" style={{ borderColor: 'rgba(26,26,26,0.12)' }}
            onKeyDown={e => e.key === 'Enter' && handleTrack()} />
          <Button data-testid="track-submit" onClick={handleTrack} disabled={loading}
            className="rounded-none font-sans text-sm gap-2" style={{ backgroundColor: '#000', color: '#fff' }}>
            <Search size={15} /> Track
          </Button>
        </div>

        {loading && <p className="text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Looking up your order...</p>}

        {!loading && searched && !order && (
          <div className="text-center py-12">
            <Package size={40} strokeWidth={1} className="mx-auto mb-3" style={{ color: 'rgba(26,26,26,0.15)' }} />
            <p className="font-sans text-base" style={{ color: 'rgba(26,26,26,0.4)' }}>Order not found</p>
          </div>
        )}

        {order && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            {/* Status Progress */}
            <div className="border rounded-lg p-6 mb-6" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-sans text-xs uppercase tracking-wider font-medium" style={{ color: '#888' }}>Order #{order.order_number}</h2>
                <span className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.4)' }}>{new Date(order.created_at).toLocaleDateString()}</span>
              </div>

              {/* Progress bar */}
              <div className="flex items-center justify-between mb-6">
                {statusSteps.map((step, i) => (
                  <div key={step} className="flex items-center flex-1">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium"
                        style={{
                          backgroundColor: i <= currentStepIdx ? '#000' : 'rgba(26,26,26,0.08)',
                          color: i <= currentStepIdx ? '#fff' : 'rgba(26,26,26,0.3)',
                        }}>
                        {i <= currentStepIdx ? <CheckCircle size={16} /> : i + 1}
                      </div>
                      <span className="font-sans text-[10px] mt-1.5 text-center" style={{ color: i <= currentStepIdx ? '#1A1A1A' : 'rgba(26,26,26,0.3)' }}>
                        {statusLabels[step]}
                      </span>
                    </div>
                    {i < statusSteps.length - 1 && (
                      <div className="flex-1 h-0.5 mx-2" style={{ backgroundColor: i < currentStepIdx ? '#000' : 'rgba(26,26,26,0.08)' }} />
                    )}
                  </div>
                ))}
              </div>

              {/* Tracking */}
              {order.tracking_number && (
                <div className="p-3 rounded" style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-sans text-xs uppercase tracking-wider mb-0.5" style={{ color: 'rgba(26,26,26,0.4)' }}>Tracking Number</p>
                      <p className="font-mono text-sm" style={{ color: '#1A1A1A' }}>{order.tracking_number}</p>
                      {order.carrier && <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>{order.carrier}</p>}
                    </div>
                    {carrierUrl && (
                      <a href={carrierUrl} target="_blank" rel="noopener noreferrer" data-testid="tracking-link"
                        className="flex items-center gap-1 font-sans text-xs font-medium" style={{ color: '#000' }}>
                        Track <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Timeline */}
            {order.status_history?.length > 0 && (
              <div className="border rounded-lg p-6 mb-6" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
                <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#888' }}>Timeline</h3>
                <div className="space-y-4">
                  {[...order.status_history].reverse().map((entry, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full mt-1.5" style={{ backgroundColor: i === 0 ? '#E85D3A' : 'rgba(26,26,26,0.15)' }} />
                        {i < order.status_history.length - 1 && <div className="w-px flex-1 mt-1" style={{ backgroundColor: 'rgba(26,26,26,0.08)' }} />}
                      </div>
                      <div className="pb-3">
                        <p className="font-sans text-sm font-medium capitalize" style={{ color: '#1A1A1A' }}>{statusLabels[entry.status] || entry.status}</p>
                        {entry.note && <p className="font-sans text-xs mt-0.5" style={{ color: 'rgba(26,26,26,0.5)' }}>{entry.note}</p>}
                        <p className="font-sans text-[10px] mt-0.5" style={{ color: 'rgba(26,26,26,0.3)' }}>
                          {new Date(entry.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Items */}
            <div className="border rounded-lg p-6" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
              <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#888' }}>Items Ordered</h3>
              <div className="space-y-3">
                {order.items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-12 flex-shrink-0 overflow-hidden" style={{ backgroundColor: 'rgba(26,26,26,0.03)' }}>
                      {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-sans text-sm" style={{ color: '#1A1A1A' }}>{item.title}</p>
                      {item.variant_title && <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.45)' }}>{item.variant_title}</p>}
                    </div>
                    <span className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>x{item.quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>
      <Footer />
    </div>
  );
}
